import { Component, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { DatePipe } from '@angular/common';
import { Subscription, combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { ChatFireService } from '../../gen2ai/chatfire.service';
import type { Conversation } from '../../models/chat.model';
import {
  DashboardAggregateResponse,
  DashboardDailyAggregate,
  EcommerceDashboardService
} from './ecommerce-dashboard.service';

export interface EcomStatCard {
  name: string;
  value: string;
  icon: string;
  trend: number;
  sparkline: number[];
  sparkColor: string;
}

export interface EcomCategorySlice {
  color: string;
  pct: number;
  label: string;
}

export interface EcomStackMonth {
  label: string;
  segments: { pct: number; color: string }[];
}

interface EcommerceDailyAggregateRow {
  day: string;
  request_count: number;
  avg_iq: number;
  politics_count: number;
  business_count: number;
  social_count: number;
  consumer_shopping_count: number;
}

function normalizeHeights(values: number[]): number[] {
  const max = Math.max(1, ...values);
  return values.map((v) => Math.round((v / max) * 100));
}

@Component({
  selector: 'app-ecommerce-dashboard',
  templateUrl: './ecommerce-dashboard.component.html',
  styleUrls: ['./ecommerce-dashboard.component.scss']
})
export class EcommerceDashboardComponent implements OnInit, OnDestroy {
  private readonly mockCards: EcomStatCard[] = [
    {
      name: 'Requests',
      value: '4,280',
      icon: 'orders',
      trend: 6.2,
      sparkline: normalizeHeights([10, 13, 18, 16, 15, 21]),
      sparkColor: '#a855f7'
    },
    {
      name: 'Avg IQ',
      value: '104.2',
      icon: 'sales',
      trend: 3.1,
      sparkline: normalizeHeights([17, 13, 16, 17, 19, 20]),
      sparkColor: '#28a745'
    },
    {
      name: 'Business',
      value: '1,420',
      icon: 'earnings',
      trend: 4.4,
      sparkline: normalizeHeights([23, 16, 25, 16, 20, 14]),
      sparkColor: '#2e69c9'
    },
    {
      name: 'Shopping',
      value: '1,180',
      icon: 'commissions-paid',
      trend: -1.2,
      sparkline: normalizeHeights([19, 22, 21, 20, 22, 20]),
      sparkColor: '#ffc200'
    }
  ];

  private readonly mockDailyRows: EcommerceDailyAggregateRow[] = [
    { day: '2026-04-24', request_count: 42, avg_iq: 108.2, politics_count: 7, business_count: 16, social_count: 10, consumer_shopping_count: 9 },
    { day: '2026-04-23', request_count: 39, avg_iq: 106.7, politics_count: 5, business_count: 15, social_count: 9, consumer_shopping_count: 10 },
    { day: '2026-04-22', request_count: 36, avg_iq: 104.9, politics_count: 6, business_count: 13, social_count: 8, consumer_shopping_count: 9 },
    { day: '2026-04-21', request_count: 31, avg_iq: 103.8, politics_count: 4, business_count: 12, social_count: 7, consumer_shopping_count: 8 },
    { day: '2026-04-20', request_count: 28, avg_iq: 102.6, politics_count: 3, business_count: 10, social_count: 8, consumer_shopping_count: 7 }
  ];

  values: EcomStatCard[] = [];

  requestTrendDayLabels: string[] = [];
  requestVolumeBars: number[] = [];
  dailyAvgIqBars: number[] = [];

  categoryMonthLabels: string[] = [];
  categoryBarsBusiness: number[] = [];
  categoryBarsPolitics: number[] = [];

  requestCategorySlices: EcomCategorySlice[] = [];

  topCategoryStacks: EcomStackMonth[] = [];

  requestsTodayBars: number[] = [];

  categoryTrendLegend = ['Business', 'Politics', 'Social', 'Shopping'];
  readonly categoryTrendColors = ['#3b86ff', '#c94a2e', '#38D997', '#ffc200'];
  categoryTrendSeries: number[][] = [[], [], [], []];

  readonly dailyAggregatesTableDisplayedColumns: string[] = [
    'day',
    'request_count',
    'avg_iq',
    'politics_count',
    'business_count',
    'social_count',
    'consumer_shopping_count'
  ];
  dailyAggregatesTableDataSource = new MatTableDataSource<EcommerceDailyAggregateRow>([]);

  syncInProgress = false;
  syncStatusMessage = '';
  private allConversationsByUid: Conversation[] = [];
  totalRequestsDisplay = '4,280';
  averageIqDisplay = '104.2';
  politicsTotalDisplay = '980';
  businessTotalDisplay = '1,420';
  socialTotalDisplay = '800';
  shoppingTotalDisplay = '1,080';

  get categoryDonutGradient(): string {
    let acc = 0;
    const stops = this.requestCategorySlices.map((s) => {
      const from = acc;
      acc += s.pct;
      return `${s.color} ${from}% ${acc}%`;
    });
    return `conic-gradient(${stops.join(', ')})`;
  }

  @ViewChild('dailyAggregatesSort', { static: true }) dailyAggregatesSort!: MatSort;
  @ViewChild('dailyAggregatesPaginator', { static: true }) dailyAggregatesPaginator!: MatPaginator;

  currentDateAndTime: string | null = '';

  private readonly subs = new Subscription();

  constructor(
    private datePipe: DatePipe,
    private chatFire: ChatFireService,
    private dashboardService: EcommerceDashboardService
  ) {
    this.applyMockDashboardState();
  }

  formatAggregateDayCell(day: string): string {
    if (day == null || typeof day !== 'string') return '';
    const trimmed = day.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return this.datePipe.transform(`${trimmed}T12:00:00`, 'EEE, MMM d, y') ?? trimmed;
    }
    const t = Date.parse(trimmed);
    return Number.isNaN(t)
      ? trimmed
      : this.datePipe.transform(new Date(t), 'EEE, MMM d, y') ?? trimmed;
  }

  ngOnInit(): void {
    this.currentDateAndTime = this.datePipe.transform(new Date(), 'MMMM dd, yyyy');

    this.dailyAggregatesTableDataSource.sortingDataAccessor = (item, property): string | number => {
      switch (property) {
        case 'day': {
          const d = String(item.day);
          return /^\d{4}-\d{2}-\d{2}$/.test(d)
            ? new Date(`${d}T12:00:00`).getTime()
            : new Date(d).getTime();
        }
        case 'request_count':
        case 'avg_iq':
        case 'politics_count':
        case 'business_count':
        case 'social_count':
        case 'consumer_shopping_count':
          return Number((item as any)[property]);
        default:
          return '';
      }
    };
    this.dailyAggregatesTableDataSource.sort = this.dailyAggregatesSort;
    this.dailyAggregatesTableDataSource.paginator = this.dailyAggregatesPaginator;

    this.subs.add(
      this.chatFire.allConversationsByUid$.subscribe((conversations: Conversation[]) => {
        this.allConversationsByUid = conversations || [];
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onSyncDashboardData(): void {
    if (this.syncInProgress) return;
    this.syncInProgress = true;
    this.syncStatusMessage = 'Syncing last month of conversation history...';

    this.subs.add(
      combineLatest([this.chatFire.uid$, this.chatFire.allConversationsByUid$])
        .pipe(
          take(1),
          filter(([uid]) => !!uid)
        )
        .subscribe({
          next: ([uid, conversations]) => {
            const resolvedUid = String(uid || '').trim();
            const isValidUid = !!resolvedUid && !resolvedUid.startsWith('anon-');
            if (!isValidUid) {
              this.applyMockDashboardState();
              this.syncStatusMessage = 'Demo mode: no authenticated UID found. Showing fallback data.';
              this.syncInProgress = false;
              return;
            }

            const monthlyPayload = this.dashboardService.buildMonthlyConversationsPayload(conversations || []);
            if (!monthlyPayload.length) {
              this.applyMockDashboardState();
              this.syncStatusMessage = 'No last-month conversation data found. Showing fallback data.';
              this.syncInProgress = false;
              return;
            }

            this.subs.add(
              this.dashboardService
                .getDashboardAggregates({
                  uid: resolvedUid,
                  conversations: monthlyPayload
                })
                .subscribe({
                  next: (response) => {
                    const hasDaily = Array.isArray(response?.daily) && response.daily.length > 0;
                    if (!hasDaily || !response?.totals) {
                      this.applyMockDashboardState();
                      this.syncStatusMessage = 'Dashboard returned no data. Showing fallback data.';
                    } else {
                      this.applyLiveDashboardState(response);
                      this.syncStatusMessage = `Synced ${response.daily.length} daily rows from the last month.`;
                    }
                    this.syncInProgress = false;
                  },
                  error: () => {
                    this.applyMockDashboardState();
                    this.syncStatusMessage = 'Sync failed (demo fallback active). Showing fallback data.';
                    this.syncInProgress = false;
                  }
                })
            );
          },
          error: () => {
            this.applyMockDashboardState();
            this.syncStatusMessage = 'Sync failed (UID/auth check failed). Showing fallback data.';
            this.syncInProgress = false;
          }
        })
    );
  }

  private applyMockDashboardState(): void {
    this.values = this.mockCards.map((card) => ({ ...card, sparkline: [...card.sparkline] }));
    this.dailyAggregatesTableDataSource.data = this.mockDailyRows.map((row) => ({ ...row }));
    this.totalRequestsDisplay = '4,280';
    this.averageIqDisplay = '104.2';
    this.politicsTotalDisplay = '980';
    this.businessTotalDisplay = '1,420';
    this.socialTotalDisplay = '800';
    this.shoppingTotalDisplay = '1,080';

    const mockRowsAsc = [...this.mockDailyRows].reverse();
    this.requestTrendDayLabels = mockRowsAsc.slice(-6).map((row) => this.dayLabel(row.day));
    this.requestVolumeBars = normalizeHeights(mockRowsAsc.slice(-6).map((row) => row.request_count));
    this.dailyAvgIqBars = normalizeHeights(mockRowsAsc.slice(-6).map((row) => row.avg_iq));

    this.requestCategorySlices = [
      { color: '#c94a2e', pct: 22, label: 'Politics' },
      { color: '#3b86ff', pct: 32, label: 'Business' },
      { color: '#38D997', pct: 18, label: 'Social' },
      { color: '#ffc200', pct: 28, label: 'Shopping' }
    ];

    this.categoryMonthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    this.categoryBarsBusiness = normalizeHeights([700, 620, 590, 720, 650, 680, 671, 690, 710, 790, 820, 790]);
    this.categoryBarsPolitics = normalizeHeights([380, 350, 420, 370, 390, 400, 410, 421, 480, 520, 500, 460]);
    this.requestsTodayBars = normalizeHeights([50, 100, 200, 300, 400, 300, 460, 430, 379, 330, 200, 120, 230, 350, 400]);

    this.categoryTrendSeries = [
      normalizeHeights([120, 132, 101, 134, 90, 230, 210, 240, 250, 220, 201, 250]),
      normalizeHeights([150, 232, 201, 154, 190, 330, 410, 440, 478, 510, 550, 470]),
      normalizeHeights([320, 332, 301, 334, 390, 330, 320, 330, 300, 380, 400, 390]),
      normalizeHeights([420, 532, 501, 534, 690, 730, 620, 770, 750, 810, 869, 900])
    ];

    this.topCategoryStacks = this.categoryMonthLabels.map((label, i) => {
      const business = this.categoryTrendSeries[0][i] || 0;
      const politics = this.categoryTrendSeries[1][i] || 0;
      const social = this.categoryTrendSeries[2][i] || 0;
      const shopping = this.categoryTrendSeries[3][i] || 0;
      const total = business + politics + social + shopping || 1;
      return {
        label,
        segments: [
          { pct: (business / total) * 100, color: this.categoryTrendColors[0] },
          { pct: (politics / total) * 100, color: this.categoryTrendColors[1] },
          { pct: (social / total) * 100, color: this.categoryTrendColors[2] },
          { pct: (shopping / total) * 100, color: this.categoryTrendColors[3] }
        ]
      };
    });
  }

  private applyLiveDashboardState(response: DashboardAggregateResponse): void {
    const dailyRows = [...(response.daily || [])]
      .map((row) => ({
        day: row.day,
        request_count: Number(row.request_count || 0),
        avg_iq: Number(row.avg_iq || 0),
        politics_count: Number(row.politics_count || 0),
        business_count: Number(row.business_count || 0),
        social_count: Number(row.social_count || 0),
        consumer_shopping_count: Number(row.consumer_shopping_count || 0)
      }))
      .sort((a, b) => (a.day > b.day ? -1 : a.day < b.day ? 1 : 0));

    this.dailyAggregatesTableDataSource.data = dailyRows;

    const totals = response.totals;
    this.values = [
      {
        name: 'Requests',
        value: this.formatNumber(totals.total_requests),
        icon: 'orders',
        trend: 0,
        sparkline: normalizeHeights(dailyRows.slice(0, 6).map((d) => d.request_count).reverse()),
        sparkColor: '#a855f7'
      },
      {
        name: 'Avg IQ',
        value: this.formatNumber(totals.average_iq, 1),
        icon: 'sales',
        trend: 0,
        sparkline: normalizeHeights(dailyRows.slice(0, 6).map((d) => d.avg_iq).reverse()),
        sparkColor: '#28a745'
      },
      {
        name: 'Business',
        value: this.formatNumber(totals.business_total),
        icon: 'earnings',
        trend: 0,
        sparkline: normalizeHeights(dailyRows.slice(0, 6).map((d) => d.business_count).reverse()),
        sparkColor: '#2e69c9'
      },
      {
        name: 'Shopping',
        value: this.formatNumber(totals.consumer_shopping_total),
        icon: 'commissions-paid',
        trend: 0,
        sparkline: normalizeHeights(dailyRows.slice(0, 6).map((d) => d.consumer_shopping_count).reverse()),
        sparkColor: '#ffc200'
      }
    ];
    this.totalRequestsDisplay = this.formatNumber(totals.total_requests);
    this.averageIqDisplay = this.formatNumber(totals.average_iq, 1);
    this.politicsTotalDisplay = this.formatNumber(totals.politics_total);
    this.businessTotalDisplay = this.formatNumber(totals.business_total);
    this.socialTotalDisplay = this.formatNumber(totals.social_total);
    this.shoppingTotalDisplay = this.formatNumber(totals.consumer_shopping_total);

    const recentAsc = [...dailyRows].reverse().slice(-6);
    this.requestTrendDayLabels = recentAsc.map((row) => this.dayLabel(row.day));
    this.requestVolumeBars = normalizeHeights(recentAsc.map((row) => row.request_count));
    this.dailyAvgIqBars = normalizeHeights(recentAsc.map((row) => row.avg_iq));

    const groupedByMonth = this.groupDailyByMonth(dailyRows);
    this.categoryMonthLabels = groupedByMonth.map((m) => m.label);
    this.categoryBarsBusiness = normalizeHeights(groupedByMonth.map((m) => m.business));
    this.categoryBarsPolitics = normalizeHeights(groupedByMonth.map((m) => m.politics));

    const totalForDonut =
      totals.politics_total +
      totals.business_total +
      totals.social_total +
      totals.consumer_shopping_total ||
      1;
    this.requestCategorySlices = [
      { color: '#c94a2e', pct: Math.round((totals.politics_total / totalForDonut) * 100), label: 'Politics' },
      { color: '#3b86ff', pct: Math.round((totals.business_total / totalForDonut) * 100), label: 'Business' },
      { color: '#38D997', pct: Math.round((totals.social_total / totalForDonut) * 100), label: 'Social' },
      { color: '#ffc200', pct: Math.round((totals.consumer_shopping_total / totalForDonut) * 100), label: 'Shopping' }
    ];

    this.requestsTodayBars = normalizeHeights(dailyRows.slice(0, 30).map((row) => row.request_count).reverse());
    this.categoryTrendSeries = [
      normalizeHeights(groupedByMonth.map((m) => m.business)),
      normalizeHeights(groupedByMonth.map((m) => m.politics)),
      normalizeHeights(groupedByMonth.map((m) => m.social)),
      normalizeHeights(groupedByMonth.map((m) => m.shopping))
    ];

    this.topCategoryStacks = groupedByMonth.map((month) => {
      const total = month.business + month.politics + month.social + month.shopping || 1;
      return {
        label: month.label,
        segments: [
          { pct: (month.business / total) * 100, color: this.categoryTrendColors[0] },
          { pct: (month.politics / total) * 100, color: this.categoryTrendColors[1] },
          { pct: (month.social / total) * 100, color: this.categoryTrendColors[2] },
          { pct: (month.shopping / total) * 100, color: this.categoryTrendColors[3] }
        ]
      };
    });
  }

  private groupDailyByMonth(rows: DashboardDailyAggregate[]): Array<{
    label: string;
    business: number;
    politics: number;
    social: number;
    shopping: number;
  }> {
    const monthMap = new Map<string, { business: number; politics: number; social: number; shopping: number }>();
    for (const row of rows) {
      const dt = new Date(`${row.day}T00:00:00`);
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      const prev = monthMap.get(key) || { business: 0, politics: 0, social: 0, shopping: 0 };
      prev.business += Number(row.business_count || 0);
      prev.politics += Number(row.politics_count || 0);
      prev.social += Number(row.social_count || 0);
      prev.shopping += Number(row.consumer_shopping_count || 0);
      monthMap.set(key, prev);
    }

    const sorted = [...monthMap.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return sorted.map(([key, value]) => {
      const [y, m] = key.split('-').map((v) => Number(v));
      const d = new Date(y, m - 1, 1);
      const label = d.toLocaleDateString(undefined, { month: 'short' });
      return { label, ...value };
    });
  }

  private dayLabel(dayIso: string): string {
    const d = new Date(`${dayIso}T00:00:00`);
    return d.toLocaleDateString(undefined, { weekday: 'short' });
  }

  private formatNumber(value: number, fractionDigits: number = 0): string {
    return Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits
    });
  }
}
